// src/components/ChatHistoryPanel.js (or your preferred path)
import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, List, ListItemButton, ListItemText,
  CircularProgress, IconButton, Paper, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Set the correct API Base URL
const API_BASE_URL = 'https://cd9d-205-254-163-120.ngrok-free.app';

// Helper function to format timestamp from conversation ID
const formatConversationIdTimestamp = (convId) => {
  if (typeof convId !== 'string') return convId; // Should not happen if IDs are consistent
  const parts = convId.split('_');
  if (parts.length >= 2 && parts[0] === 'chat') {
    const timestamp = parseInt(parts[1], 10);
    if (!isNaN(timestamp)) {
      try {
        return new Date(timestamp).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (e) {
        console.error("Error formatting date:", e);
        return convId; // Fallback to full ID if formatting fails
      }
    }
  }
  return convId; // Fallback to full ID if not in expected format
};


const fetchChatHistoryList = async (userId) => {
  const url = `${API_BASE_URL}/fetch_chathistory_list`;
  console.log(`Fetching chat history list from: ${url} with user_id: ${userId}`);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId }),
    });

    if (!response.ok) {
      let errorData = `HTTP error! status: ${response.status}`;
      try {
        const errorJson = await response.json();
        errorData += `, Message: ${errorJson.detail || errorJson.message || JSON.stringify(errorJson)}`;
      } catch (e) {
        try {
            const errorText = await response.text();
            errorData += `, Body: ${errorText}`;
        } catch (textError) {
            errorData += `, Could not read error response body.`;
        }
      }
      console.error('fetchChatHistoryList error response:', response, 'Error data:', errorData);
      throw new Error(errorData);
    }
    return response.json();
  } catch (error) {
    console.error('Network or other error in fetchChatHistoryList:', error);
    throw error;
  }
};

const fetchChatByConversation = async (userId, conversationId) => {
  const url = `${API_BASE_URL}/fetch_chat_by_conversation`;
  console.log(`Fetching chat by conversation from: ${url} with user_id: ${userId}, conversation_id: ${conversationId}`);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId, conversation_id: conversationId }),
    });

    if (!response.ok) {
      let errorData = `HTTP error! status: ${response.status}`;
      try {
        const errorJson = await response.json();
        errorData += `, Message: ${errorJson.detail || errorJson.message || JSON.stringify(errorJson)}`;
      } catch (e) {
         try {
            const errorText = await response.text();
            errorData += `, Body: ${errorText}`;
        } catch (textError) {
            errorData += `, Could not read error response body.`;
        }
      }
      console.error('fetchChatByConversation error response:', response, 'Error data:', errorData);
      throw new Error(errorData);
    }
    return response.json();
  } catch (error) {
    console.error('Network or other error in fetchChatByConversation:', error);
    throw error;
  }
};

const ChatHistoryPanel = ({ isOpen, onClose, userId }) => {
  const [conversationIdList, setConversationIdList] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [listError, setListError] = useState(null);
  const [messagesError, setMessagesError] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      setIsLoadingList(true);
      setListError(null);
      setConversationIdList([]);
      setSelectedConversationId(null);
      setChatMessages([]);

      fetchChatHistoryList(userId)
        .then(data => {
          const ids = Array.isArray(data) ? data : (data.conversation_ids || []);
          if (!Array.isArray(ids)) {
            console.error("fetchChatHistoryList response is not in expected format (array or {conversation_ids: array}):", data);
            throw new Error("Invalid data format for conversation list.");
          }
          setConversationIdList(ids);
          setIsLoadingList(false);
        })
        .catch(err => {
          console.error("Error in useEffect loading history list:", err);
          setListError(err.message || 'Failed to load history list. Check console for details.');
          setIsLoadingList(false);
        });
    } else if (!isOpen) {
      setConversationIdList([]);
      setSelectedConversationId(null);
      setChatMessages([]);
      setListError(null);
      setMessagesError(null);
    }
  }, [isOpen, userId]);

  const handleSelectConversation = (convId) => {
    if (selectedConversationId === convId && chatMessages.length > 0 && !messagesError) {
        return;
    }
    setSelectedConversationId(convId);
    setIsLoadingMessages(true);
    setMessagesError(null);
    setChatMessages([]);

    fetchChatByConversation(userId, convId)
      .then(data => {
        if (!Array.isArray(data)) {
            console.error("fetchChatByConversation response is not an array:", data);
            throw new Error("Invalid data format for conversation messages.");
        }
        setChatMessages(data || []);
        setIsLoadingMessages(false);
      })
      .catch(err => {
        console.error(`Error in handleSelectConversation for ${convId}:`, err);
        setMessagesError(err.message || `Failed to load messages for ${convId}. Check console.`);
        setIsLoadingMessages(false);
      });
  };

  const renderMessages = () => {
    if (isLoadingMessages) {
      return <CircularProgress size={24} sx={{ display: 'block', margin: '20px auto' }} />;
    }
    if (messagesError) {
        return <Alert severity="error" sx={{ m: 2 }}>{messagesError}</Alert>;
    }
    if (!chatMessages.length && selectedConversationId) {
      return <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>No messages found for this conversation.</Typography>;
    }
    if (!chatMessages.length) {
      return null;
    }

    return (
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, backgroundColor: '#2e2e2e' }}>
        {chatMessages.map((interaction, index) => {
          const userMessage = interaction.user || interaction.User;
          const botMessage = interaction.Alex || interaction.alex || interaction.Bot || interaction.bot;

          return (
            <React.Fragment key={index}>
              {userMessage && (
                <Paper elevation={1} sx={{ p: 1.5, mb: 1, bgcolor: '#007bff', color: 'white', textAlign: 'left', marginLeft: 'auto', maxWidth: '80%', float: 'right', clear: 'both' }}>
                  <Typography variant="body2"><strong>You:</strong> {userMessage}</Typography>
                </Paper>
              )}
              {botMessage && (
                <Paper elevation={1} sx={{ p: 1.5, mb: 1, bgcolor: '#3c3c3e', color: 'white', textAlign: 'left', marginRight: 'auto', maxWidth: '80%', float: 'left', clear: 'both' }}>
                  <Typography variant="body2"><strong>Alex:</strong> {botMessage}</Typography>
                </Paper>
              )}
               {!(userMessage || botMessage) && (
                 <Paper elevation={1} sx={{p:1.5, mb: 1, bgcolor: '#444', color: 'white', fontStyle: 'italic', clear: 'both'}}>
                    <Typography variant="body2">Empty message entry</Typography>
                 </Paper>
               )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '90%', sm: 400, md: 500 }, backgroundColor: '#1e1e1e', color: 'white', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333' }}>
        <Typography variant="h6">Chat History</Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {listError && <Alert severity="error" sx={{ m: 1 }}>{listError}</Alert>}

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '40%', borderRight: '1px solid #333', overflowY: 'auto' }}>
          {isLoadingList ? (
            <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
          ) : (
            <List disablePadding>
              {conversationIdList.length > 0 ? conversationIdList.map(convId => (
                <ListItemButton
                  key={convId}
                  selected={selectedConversationId === convId}
                  onClick={() => handleSelectConversation(convId)}
                  sx={{
                    borderBottom: '1px solid #2a2a2a',
                    '&.Mui-selected': { backgroundColor: '#007bff', '&:hover': { backgroundColor: '#006ce0'}},
                    '&:hover': { backgroundColor: '#2c2c2e' }
                  }}
                >
                  {/* Use the formatting function here */}
                  <ListItemText 
                    primary={formatConversationIdTimestamp(convId)} 
                    primaryTypographyProps={{ 
                        sx: {
                            fontSize: '0.875rem', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis'
                        }
                    }}
                  />
                </ListItemButton>
              )) : (
                !isLoadingList && !listError && <Typography sx={{ p: 2, color: 'text.secondary', textAlign:'center' }}>No history found.</Typography>
              )}
            </List>
          )}
        </Box>
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
            {selectedConversationId || messagesError ?
                renderMessages() :
                <Typography sx={{p:2, textAlign:'center', color:'text.secondary', marginTop: 2}}>Select a conversation to view messages.</Typography>}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatHistoryPanel;
